import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaDownload, FaPlus } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import './Bookings.css';
import Pagination from '../Common/Pagination';

const BookingsList = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [dateRange, setDateRange] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');

  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [customers, setCustomers] = useState([]);

  const handleClearFilters = () => {
    setDateRange('');
    setStatusFilter('');
    setCategoryFilter('');
    setProviderFilter('');
    setCustomerFilter('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleExport = async () => {
    try {
      const params = {
        limit: 10000,
        status: statusFilter || undefined,
        categoryId: categoryFilter || undefined,
        providerId: providerFilter || undefined,
        customerId: customerFilter || undefined,
      };

      // Handle Date Ranges
      const today = new Date();
      if (dateRange === 'today') {
        params.startDate = today.toISOString().split('T')[0];
        params.endDate = params.startDate;
      } else if (dateRange === 'week') {
        const lastWeek = new Date();
        lastWeek.setDate(today.getDate() - 7);
        params.startDate = lastWeek.toISOString().split('T')[0];
        params.endDate = today.toISOString().split('T')[0];
      } else if (dateRange === 'month') {
        const lastMonth = new Date();
        lastMonth.setMonth(today.getMonth() - 1);
        params.startDate = lastMonth.toISOString().split('T')[0];
        params.endDate = today.toISOString().split('T')[0];
      }

      // Fetch all bookings with current filters
      const response = await api.get(ENDPOINTS.BOOKINGS.base, { params });
      const allBookings = response.data?.data || response.data || [];

      const rows = allBookings.map((b, index) => ({
        'No.': index + 1,
        'Booking ID': b.bookingNumber || b.id,
        'Date': new Date(b.scheduledDate).toLocaleDateString(),
        'Time': b.scheduledTime || '',
        'Customer Name': `${b.customer?.firstName || ''} ${b.customer?.lastName || ''}`.trim(),
        'Customer Email': b.customer?.email || '',
        'Provider Name': `${b.provider?.firstName || ''} ${b.provider?.lastName || ''}`.trim(),
        'Provider Email': b.provider?.email || '',
        'Service': b.service?.name || 'General Service',
        'Amount ($)': Number(b.totalAmount || 0).toFixed(2),
        'Status': b.status || '',
        'Created At': new Date(b.createdAt).toLocaleDateString(),
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();

      // Set column widths
      worksheet['!cols'] = [
        { wch: 5 }, { wch: 38 }, { wch: 14 }, { wch: 10 },
        { wch: 20 }, { wch: 28 }, { wch: 20 }, { wch: 28 },
        { wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 14 },
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Bookings');
      XLSX.writeFile(workbook, `TinyCare_Bookings_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export bookings. Please try again.');
    }
  };
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  const fetchMetadata = useCallback(async () => {
    try {
      const [catsRes, provsRes, custsRes] = await Promise.all([
        api.get(ENDPOINTS.SERVICES.CATEGORIES),
        api.get(ENDPOINTS.PROVIDERS.base, { params: { limit: 100 } }),
        api.get(ENDPOINTS.CUSTOMERS.base, { params: { limit: 100 } })
      ]);
      setCategories(catsRes.data || []);
      setProviders(provsRes.data?.data || provsRes.data || []);
      setCustomers(custsRes.data?.data || custsRes.data || []);
    } catch (e) {
      console.error('Metadata fetch failed:', e);
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter || undefined,
        categoryId: categoryFilter || undefined,
        providerId: providerFilter || undefined,
        customerId: customerFilter || undefined,
      };

      // Handle Date Ranges
      const today = new Date();
      if (dateRange === 'today') {
        params.startDate = today.toISOString().split('T')[0];
        params.endDate = params.startDate;
      } else if (dateRange === 'week') {
        const lastWeek = new Date();
        lastWeek.setDate(today.getDate() - 7);
        params.startDate = lastWeek.toISOString().split('T')[0];
        params.endDate = today.toISOString().split('T')[0];
      } else if (dateRange === 'month') {
        const lastMonth = new Date();
        lastMonth.setMonth(today.getMonth() - 1);
        params.startDate = lastMonth.toISOString().split('T')[0];
        params.endDate = today.toISOString().split('T')[0];
      }

      const response = await api.get(ENDPOINTS.BOOKINGS.base, { params });
      const bookingsData = response.data || [];
      setBookings(bookingsData.data || bookingsData);
      setPagination(prev => ({ ...prev, total: bookingsData.meta?.total || response.total || 0 }));
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, statusFilter, categoryFilter, providerFilter, customerFilter, dateRange]);

  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const getStatusClass = (status) => {
    const s = status ? status.toLowerCase() : '';
    const classes = {
      'confirmed': 'status-approved',
      'pending': 'status-pending',
      'cancelled': 'status-cancelled',
      'completed': 'status-completed',
      'in_progress': 'status-pending'
    };
    return classes[s] || '';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Fetching bookings...</p>
      </div>
    );
  }

  return (
    <div className="bookings-page">
      <div className="page-header">
        <div>
          <h1>Bookings</h1>
          <p className="page-subtitle">Manage and monitor all platform bookings</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-success" onClick={handleExport}>
            <FaDownload /> Export Excel
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="content-card filters-section">
        <div className="filters-row">
          {/* Date Range Filter */}
          <div className="filter-group">
            <select
              value={dateRange}
              onChange={(e) => { setDateRange(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="filter-select"
            >
              <option value="">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="filter-group">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="filter-select"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Category Filter */}
          <div className="filter-group">
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="filter-select"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Provider Filter */}
          <div className="filter-group">
            <select
              value={providerFilter}
              onChange={(e) => { setProviderFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="filter-select"
            >
                <option value="">All Providers</option>
                {providers.map(p => (
                  <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                ))}
            </select>
          </div>

          {/* Customer Filter */}
          <div className="filter-group">
            <select
              value={customerFilter}
              onChange={(e) => { setCustomerFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="filter-select"
            >
                <option value="">All Customers</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                ))}
            </select>
          </div>

          <button className="btn-clear-premium" onClick={handleClearFilters}>
            Clear Filters
          </button>
          {/* <button className="btn btn-primary" onClick={handleExport}>
            <FaDownload /> Export (CSV / Excel)
          </button> */}
        </div>
      </div>

      {/* Bookings Table */}
      <div className="content-card">
        <div className="table-responsive">
          <table className="table table-hover align-middle custom-table">
            <thead>
              <tr>
                <th>BOOKING ID</th>
                <th>DATE / TIME</th>
                <th>CUSTOMER</th>
                <th>PROVIDER</th>
                <th className="text-center">THERAPY</th>
                <th className="text-center">AMOUNT</th>
                <th className="text-center">STATUS</th>
                <th className="text-center">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-state">
                    <div className="empty-state-content">
                      <div className="empty-state-icon">📅</div>
                      <h3>No bookings found</h3>
                      <p>Try adjusting your search or filter criteria</p>
                    </div>
                  </td>
                </tr>
              ) : (
                bookings.map((booking) => (
                  <tr
                    key={booking.id}
                    onClick={() => navigate(`/bookings/${booking.id}`)}
                    style={{ cursor: 'pointer' }}
                    className="booking-row-clickable"
                  >
                    <td>
                      <span className="booking-id">{booking.bookingNumber}</span>
                    </td>
                    <td>{new Date(booking.scheduledDate).toLocaleDateString()} {booking.scheduledTime}</td>
                    <td>
                      <div className="user-cell">
                        <img
                          src={booking.customer?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(booking.customer?.firstName || '')}+${encodeURIComponent(booking.customer?.lastName || '')}&background=50C8AC&color=fff&bold=true`}
                          alt={booking.customer?.firstName}
                          className="user-avatar"
                          onError={(e) => { 
                            e.target.onerror = null; 
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(booking.customer?.firstName || 'U')}&background=50C8AC&color=fff&bold=true`; 
                          }}
                        />

                        <span>{booking.customer?.firstName} {booking.customer?.lastName}</span>
                      </div>
                    </td>
                    <td>
                      <div className="user-cell">
                        <img
                          src={booking.provider?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(booking.provider?.firstName || '')}+${encodeURIComponent(booking.provider?.lastName || '')}&background=4834d4&color=fff&bold=true`}
                          alt={booking.provider?.firstName}
                          className="user-avatar"
                          onError={(e) => { 
                            e.target.onerror = null; 
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(booking.provider?.firstName || 'P')}&background=4834d4&color=fff&bold=true`; 
                          }}
                        />

                        <span>{booking.provider?.firstName} {booking.provider?.lastName}</span>
                      </div>
                    </td>
                    <td className="text-center">{booking.service?.name || 'General Service'}</td>
                    <td>
                      <div className="amount-container" style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                        <span className="amount">${Number(booking.totalAmount).toFixed(2)}</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                          {booking.isDeposit && (
                            <span className="badge-nanny-tag" style={{ fontSize: '10px', backgroundColor: '#f0f9ff', color: '#0369a1', padding: '1px 6px', borderRadius: '4px', fontWeight: 'bold', border: '1px solid #bae6fd', whiteSpace: 'nowrap' }}>
                              NANNY
                            </span>
                          )}
                          {/* {(booking.packageType === 'monthly' || (booking.service?.name || '').toLowerCase().includes('monthly')) && (
                            <span className="badge-monthly-tag" style={{ fontSize: '10px', backgroundColor: '#fdf2f8', color: '#9d174d', padding: '1px 6px', borderRadius: '4px', fontWeight: 'bold', border: '1px solid #fbcfe8', whiteSpace: 'nowrap' }}>
                              MONTHLY
                            </span>
                          )} */}
                        </div>
                      </div>
                    </td>
                    <td className="text-center">
                      <span className={`status-badge ${getStatusClass(booking.status)}`}>
                        {(booking.status || '').toUpperCase() === 'COMPLETED' ? 'Completed' : booking.status}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="action-buttons" style={{ justifyContent: 'center' }}>
                        <button
                          className="btn-icon"
                          title="View Details"
                          onClick={(e) => { e.stopPropagation(); navigate(`/bookings/${booking.id}`); }}
                        >
                          <FaEye />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination 
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        />
      </div>
    </div>
  );
};

export default BookingsList;